import { CSSProperties } from 'react';
import { ImageSection, LogoSection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { Corners, FrameAlignment } from '../../content-composer/types';
import { JustifiedLayoutType, ImageWithDimensions } from '../types';
import { getAlignment, getCorners, getNonNegativeNumber } from '../utils/frame-utils';
import { getCornersStyle } from '../utils/get-corners-style';
import { EMPTY_IMAGE_SIZE } from '../utils/image-dimensions';
import { calculateHorizontalJustifiedLayout, getJustifiedLayoutPaddingStyle } from '../utils/justified-layout-utils';
import { EmailTable } from './email-table';

type JustifiedLayoutProps = {
  alignment?: string;
  corners?: string;
  height?: string;
  imageSpacing?: number;
  images: ImageWithDimensions[];
  onLoad?: (imageIndex: number, width: number, height: number) => void;
  onRemove?: (imageIndex: number) => void;
  onReplace?: (imageIndex: number) => void;
  onSelect?: (imageIndex?: number) => void;
  selectedImageIndex?: number;
  type?: JustifiedLayoutType;
};

export const JustifiedLayout = ({
  height: passedHeight,
  alignment: passedAlignment,
  corners: passedCorners,
  imageSpacing: passedImageSpacing,
  type = 'image',
  ...props
}: JustifiedLayoutProps) => {
  const alignment = getAlignment(passedAlignment);
  const corners = getCorners(passedCorners);
  const height = getNonNegativeNumber(passedHeight);
  const imageSpacing = getNonNegativeNumber(passedImageSpacing);

  return height !== undefined ? (
    <HorizontalJustifiedLayout
      alignment={alignment}
      corners={corners}
      height={height}
      imageSpacing={imageSpacing}
      type={type}
      {...props}
    />
  ) : (
    <VerticalJustifiedLayout
      alignment={alignment}
      corners={corners}
      imageSpacing={imageSpacing}
      type={type}
      {...props}
    />
  );
};

type TypedJustifiedLayoutProps = Omit<
  JustifiedLayoutProps,
  'alignment' | 'corners' | 'height' | 'imageSpacing' | 'type'
> & {
  alignment: FrameAlignment;
  corners: Corners;
  height?: number;
  imageSpacing?: number;
  type: JustifiedLayoutType;
};

type HorizontalJustifiedLayoutProps = Omit<TypedJustifiedLayoutProps, 'height'> & {
  height: number;
};

const HorizontalJustifiedLayout = ({
  alignment,
  corners,
  height,
  imageSpacing,
  images,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  selectedImageIndex,
  type,
}: HorizontalJustifiedLayoutProps) => {
  const { isSelected: isSectionSelected } = useComposerSection<ImageSection | LogoSection>();
  const { percentageWidths, percentageSpacing, maxTableWidth } = calculateHorizontalJustifiedLayout(
    images,
    height,
    imageSpacing
  );
  const isEmpty = images.length === 0 || images.every((image) => !image.url);

  return (
    <EmailTable
      alignment={alignment}
      style={{
        height: isEmpty && onSelect ? `${EMPTY_IMAGE_SIZE}px` : undefined,
        maxWidth: maxTableWidth,
        width: '100%',
      }}
    >
      <EmailTable.Row>
        {percentageWidths.map((percentageWidth, index) => (
          <EmailTable.Cell
            align={alignment}
            key={index}
            style={{
              ...getJustifiedLayoutPaddingStyle(index, images.length, percentageSpacing),
              height: onSelect && !images[index]?.url ? 0 : undefined, // Makes the cell "growable" so the Add Image tile looks decent
              position: 'relative',
              textAlign: alignment,
              width: percentageWidth,
            }}
            onClick={onSelect ? (e) => e.stopPropagation() : undefined}
          >
            <JustifiedImage
              alt={images[index]?.name}
              isAlone={images.length === 1}
              isSelected={isSectionSelected && selectedImageIndex === index}
              onLoad={onLoad ? (width, height) => onLoad(index, width, height) : undefined}
              onRemove={onRemove ? () => onRemove(index) : undefined}
              onReplace={onReplace ? () => onReplace(index) : undefined}
              onSelect={onSelect ? () => onSelect(index) : undefined}
              src={images[index]?.url}
              style={{
                // TODO: The corner styling needs to be adjusted for the table padding that's used to space things out
                ...getCornersStyle<CSSProperties>(corners),
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              type={type}
            />
          </EmailTable.Cell>
        ))}
      </EmailTable.Row>
    </EmailTable>
  );
};

type VerticalJustifiedLayoutProps = Omit<TypedJustifiedLayoutProps, 'height'>;

const VerticalJustifiedLayout = ({
  alignment,
  corners,
  imageSpacing,
  images,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  selectedImageIndex,
  type,
}: VerticalJustifiedLayoutProps) => {
  const { isSelected: isSectionSelected } = useComposerSection<ImageSection | LogoSection>();
  const isInteractive = !!onLoad || !!onRemove || !!onReplace || !!onSelect;

  return images.length ? (
    <EmailTable alignment={alignment} style={{ width: '100%', maxWidth: '100%' }}>
      <EmailTable.Row>
        <EmailTable.Cell alignment={alignment}>
          {images.map((image, index) => (
            <OptionalContainer
              key={index}
              onClick={(e) => e.stopPropagation()}
              render={isInteractive}
              style={{
                position: 'relative',
                height: !image.url ? `${EMPTY_IMAGE_SIZE}px` : undefined, // Creates the "tile" for the Add Image button
              }}
            >
              <JustifiedImage
                alt={image.name}
                isAlone={images.length === 1}
                isSelected={isSectionSelected && selectedImageIndex === index}
                onLoad={onLoad ? (width, height) => onLoad(index, width, height) : undefined}
                onRemove={onRemove ? () => onRemove(index) : undefined}
                onReplace={onReplace ? () => onReplace(index) : undefined}
                onSelect={onSelect ? () => onSelect(index) : undefined}
                src={image.url}
                style={{
                  ...getJustifiedLayoutPaddingStyle(index, images.length, imageSpacing, 'px', true),
                  // TODO: The corner styling needs to be adjusted for the table padding that's used to space things out
                  ...getCornersStyle<CSSProperties>(corners),
                  width: '100%',
                  maxWidth: image.width + 'px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                }}
                type={type}
              />
            </OptionalContainer>
          ))}
        </EmailTable.Cell>
      </EmailTable.Row>
    </EmailTable>
  ) : null;
};

type OptionalContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  render: boolean;
};

const OptionalContainer = ({ render, children, ...props }: OptionalContainerProps) =>
  render ? <div {...props}>{children}</div> : <>{children}</>;

type JustifiedImageProps = {
  alt?: string;
  isAlone?: boolean;
  isSelected?: boolean;
  onLoad?: (width: number, height: number) => void;
  onRemove?: () => void;
  onReplace?: () => void;
  onSelect?: () => void;
  src?: string;
  style?: CSSProperties;
  type: JustifiedLayoutType;
};

const JustifiedImage = ({
  alt,
  isAlone,
  isSelected,
  onLoad,
  onRemove,
  onReplace,
  onSelect,
  src,
  style,
  type,
}: JustifiedImageProps) => {
  const { t } = useTranslation('content-composer');

  // TODO: Handle load failures. This means the image has failed to load:
  // img.complete === true && img.naturalWidth === 0 && img.naturalHeight === 0
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    onLoad?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight);
  };

  const image = (
    <img
      alt={alt}
      data-type='src-image'
      onClick={onSelect}
      onLoad={onLoad ? handleLoad : undefined}
      src={src}
      style={style}
    />
  );

  if (!onSelect) return src ? image : null;

  return src ? (
    <>
      {image}
      {isSelected && (
        <SectionBase.Toolbar position='bottom-center' disableResponsiveness>
          {onReplace && <SectionBase.Toolbar.Button onClick={onReplace}>{t('Replace')}</SectionBase.Toolbar.Button>}
          {onRemove && <SectionBase.Toolbar.Button onClick={onRemove}>{t('Remove')}</SectionBase.Toolbar.Button>}
        </SectionBase.Toolbar>
      )}
    </>
  ) : (
    <div
      style={{
        // The justified layout uses padding for margin which gets weird when applied to the Add Image button tile.
        // Converting it to margin makes it look acceptable.
        margin: style?.padding,
        marginBottom: style?.paddingBottom,
        marginLeft: style?.paddingLeft,
        marginRight: style?.paddingRight,
        marginTop: style?.paddingTop,
        // End padding-to-margin conversion

        alignItems: 'center',
        border: isAlone ? 0 : `solid 1px ${theme.colors.neutral20}`,
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        padding: theme.spacing(1),
      }}
    >
      <Button
        css={{ background: theme.colors.neutral5, flexShrink: 0, margin: theme.spacing(1) }}
        iconName='plus-small'
        onClick={onReplace}
        size='small'
        variant='secondary'
      >
        {type === 'logo' ? t('Add Logo') : t('Add Image')}
      </Button>
    </div>
  );
};
